import { useTranslation } from 'react-i18next'
import { type Field, useFields } from '@pbt/pbt-ui-components'

import type {
  CreateInventoryOrderItemReceiptInput,
  InventoryOrderItemReceipt,
} from '~/api/graphql/generated/types'

export interface ReceiptFields {
  businessReferenceNumber: Field
  damageNotes: Field
  damageReasonId: Field
  damaged: Field
  expirationDate: Field
  lotNumber: Field
  receivedQuantity: Field
  serialNumber: Field
}

export interface ReceiptFieldGroup {
  fields: ReceiptFields
  id: string
}

interface DraftReceipt extends CreateInventoryOrderItemReceiptInput {
  id: string
}

const hasDamageReason = (
  receipt: InventoryOrderItemReceipt | DraftReceipt,
): receipt is InventoryOrderItemReceipt =>
  'damageReason' in receipt && receipt.damageReason?.id !== undefined

export const useReceiptFields = (
  receipts: Array<InventoryOrderItemReceipt | DraftReceipt>,
) => {
  const { t } = useTranslation('Admin')

  const { fields, validate, touched } = useFields(
    receipts.flatMap((receipt) => [
      {
        name: `${receipt.id}-receivedQuantity`,
        label: t('Admin:CATALOG.ORDERS.RECEIVING_QUANTITY_LABEL'),
        validators: ['required', 'greaterThanZero'],
        initialValue: receipt.receivedQuantity,
      },
      {
        name: `${receipt.id}-lotNumber`,
        label: t('Admin:LOT_NUMBER'),
        initialValue: receipt.lotNumber,
      },
      {
        name: `${receipt.id}-expirationDate`,
        label: t('Admin:EXPIRATION_DATE'),
        initialValue: receipt.expirationDate,
      },
      {
        name: `${receipt.id}-serialNumber`,
        label: t('Admin:SERIAL_NUMBER'),
        initialValue: receipt.serialNumber,
      },
      {
        name: `${receipt.id}-businessReferenceNumber`,
        label: t('Common:PRACTICE_REFERENCE'),
        initialValue: receipt.businessReferenceNumber,
      },
      {
        name: `${receipt.id}-damaged`,
        label: t('Admin:CATALOG.ORDERS.DAMAGED_TOGGLE_LABEL'),
        initialValue: Boolean(
          hasDamageReason(receipt)
            ? receipt.damageReason?.id
            : receipt.damagedReasonId,
        ),
        type: 'toggle',
      },
      {
        name: `${receipt.id}-damageReasonId`,
        label: t('Admin:CATALOG.ORDERS.DAMAGE_REASON_LABEL'),
        initialValue: hasDamageReason(receipt)
          ? receipt.damageReason?.id
          : receipt.damagedReasonId,
      },
      {
        name: `${receipt.id}-damageNotes`,
        label: t('Admin:CATALOG.ORDERS.DAMAGE_NOTES_LABEL'),
        initialValue: receipt.damageNotes,
      },
    ]),
  )

  return {
    receiptFields: receipts.map(({ id }) => ({
      id,
      fields: {
        receivedQuantity: fields[`${id}-receivedQuantity`],
        lotNumber: fields[`${id}-lotNumber`],
        expirationDate: fields[`${id}-expirationDate`],
        serialNumber: fields[`${id}-serialNumber`],
        businessReferenceNumber: fields[`${id}-businessReferenceNumber`],
        damaged: fields[`${id}-damaged`],
        damageReasonId: fields[`${id}-damageReasonId`],
        damageNotes: fields[`${id}-damageNotes`],
      },
    })),
    validate,
    touched,
  }
}
